import { createFileRoute } from "@tanstack/react-router";

import { MainLayout } from "@/components/layouts/main-layout";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-picker-with-range";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { AccountPeriodSheet } from "@/features/dashboard/account-period-sheet";
import { formatAmount, formatPercentage } from "@/lib/utils";
import { useGetAccountPeriodBalances } from "@/queries/ledger/accounts/get-period-balances";
import type { PeriodGranularity } from "@/queries/ledger/accounts/get-period-balances";
import {
  endOfMonth,
  startOfMonth,
  startOfQuarter,
  subMonths,
  subQuarters,
} from "date-fns";
import { Calendar, Percent, Search, TrendingUp } from "lucide-react";
import { useMemo, useState } from "react";
import type { DateRange } from "react-day-picker";

export const Route = createFileRoute(
  "/_authenticated/$company/unternehmen/reporting/accounts/",
)({
  component: AccountsListPage,
});

function formatPeriodLabel(periodKey: string): string {
  // Quarterly format: YYYY-QN
  if (periodKey.includes("-Q")) {
    const [year, quarter] = periodKey.split("-Q");
    return `Q${quarter} ${year}`;
  }

  // Monthly format: YYYY-MM
  const [year, month] = periodKey.split("-");
  const date = new Date(
    Number.parseInt(year, 10),
    Number.parseInt(month, 10) - 1,
    1,
  );
  const monthNames = [
    "Jan",
    "Feb",
    "Mär",
    "Apr",
    "Mai",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Okt",
    "Nov",
    "Dez",
  ];
  return `${monthNames[date.getMonth()]} ${date.getFullYear()}`;
}

function AccountsListPage() {
  const [searchQuery, setSearchQuery] = useState("");

  // Period controls
  const defaultDateRange = {
    from: startOfMonth(subMonths(new Date(), 2)),
    to: endOfMonth(new Date()),
  };
  const [dateRange, setDateRange] = useState<DateRange | undefined | null>(
    defaultDateRange,
  );
  const [granularity, setGranularity] = useState<PeriodGranularity>("monthly");
  const [showOnlyActive, setShowOnlyActive] = useState(true);
  const [viewMode, setViewMode] = useState<"absolute" | "percentage">(
    "absolute",
  );

  // Sheet state for viewing period details
  const [sheetState, setSheetState] = useState<{
    open: boolean;
    accountId: string | null;
    accountNumber: string | null;
    accountName: string | null;
    periodStartDate: Date | string | null;
    periodEndDate: Date | string | null;
    periodLabel: string | null;
  }>({
    open: false,
    accountId: null,
    accountNumber: null,
    accountName: null,
    periodStartDate: null,
    periodEndDate: null,
    periodLabel: null,
  });

  // Calculate extended date range that includes one previous period for comparison
  const extendedDateRange = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return null;

    // Calculate one period before the selected start date
    let extendedFrom: Date;
    switch (granularity) {
      case "monthly":
        extendedFrom = startOfMonth(subMonths(dateRange.from, 1));
        break;
      case "quarterly":
        extendedFrom = startOfQuarter(subQuarters(dateRange.from, 1));
        break;
      default:
        extendedFrom = startOfMonth(subMonths(dateRange.from, 1));
    }

    return {
      from: extendedFrom,
      to: dateRange.to,
    };
  }, [dateRange, granularity]);

  // Fetch all period balances including previous period for comparison
  const {
    data: allAccountPeriodBalances,
    loading,
    error,
  } = useGetAccountPeriodBalances({
    startDate: extendedDateRange?.from,
    endDate: extendedDateRange?.to,
    granularity,
    includeInactive: !showOnlyActive,
    excludeCounterparty: true,
  });

  // Split into full data (with previous period) and displayed data (without previous period)
  const { accountPeriodBalances, displayedPeriodBalances } = useMemo(() => {
    if (!allAccountPeriodBalances || !dateRange?.from || !dateRange?.to) {
      return { accountPeriodBalances: [], displayedPeriodBalances: [] };
    }

    // Create a version with only the selected periods for display
    const displayBalances = allAccountPeriodBalances.map((account) => ({
      ...account,
      periodBalances: account.periodBalances.filter(
        (period) =>
          dateRange.from &&
          dateRange.to &&
          new Date(period.startDate) >= dateRange.from &&
          new Date(period.endDate) <= dateRange.to,
      ),
    }));

    return {
      accountPeriodBalances: allAccountPeriodBalances,
      displayedPeriodBalances: displayBalances,
    };
  }, [allAccountPeriodBalances, dateRange]);

  // Filter by search query
  const filteredAccounts = useMemo(() => {
    if (!displayedPeriodBalances) return [];

    // Filter by search query
    if (!searchQuery.trim()) return displayedPeriodBalances;

    const query = searchQuery.toLowerCase();
    return displayedPeriodBalances.filter((account) => {
      const number = account.accountNumber?.toString().toLowerCase() || "";
      const name = account.accountName?.toLowerCase() || "";
      return number.includes(query) || name.includes(query);
    });
  }, [displayedPeriodBalances, searchQuery]);

  const handlePeriodClick = (
    e: React.MouseEvent,
    accountId: string,
    accountNumber: string,
    accountName: string,
    periodStartDate: Date | string,
    periodEndDate: Date | string,
    periodLabel: string,
  ) => {
    e.stopPropagation();
    // Open sheet with period details
    setSheetState({
      open: true,
      accountId,
      accountNumber,
      accountName,
      periodStartDate,
      periodEndDate,
      periodLabel,
    });
  };

  // Helper function to calculate percentage change
  const calculatePercentageChange = (
    current: number,
    previous: number,
  ): number | null => {
    if (!Number.isFinite(current) || !Number.isFinite(previous)) {
      return null;
    }

    if (previous === 0 && current === 0) {
      return null;
    }

    if (previous === 0) {
      // If previous is 0 and current is not 0, it's infinite growth
      return current !== 0 ? null : 0;
    }

    const result = ((current - previous) / Math.abs(previous)) * 100;

    // Ensure result is finite
    if (!Number.isFinite(result)) {
      return null;
    }

    return result;
  };

  return (
    <MainLayout>
      <div className="flex flex-col h-full overflow-auto bg-muted/30">
        <div className="flex-1 p-6 lg:p-8 space-y-8">
          {/* Header */}
          <div className="space-y-3">
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text">
              Periodenübersicht
            </h1>
          </div>

          {/* Controls */}
          <div className="flex flex-wrap gap-4 items-end">
            {/* Search Bar */}
            <div className="relative flex-1 min-w-[240px] max-w-md">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground"
                size={18}
              />
              <Input
                type="text"
                placeholder="Konto suchen (Nummer oder Name)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Date Range Picker */}
            <div>
              <DatePickerWithRange
                value={
                  dateRange?.from && dateRange?.to
                    ? { from: dateRange.from, to: dateRange.to }
                    : undefined
                }
                onChange={(range) => setDateRange(range)}
                placeholder="Zeitraum auswählen"
                Icon={Calendar}
                highlightWhenSelected={true}
              />
            </div>

            {/* Granularity Selector */}
            <div className="w-[180px]">
              <Select
                value={granularity}
                onValueChange={(value) =>
                  setGranularity(value as PeriodGranularity)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Granularität" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monatlich</SelectItem>
                  <SelectItem value="quarterly">Quartalsweise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Show Only Active Toggle */}
            <Button
              variant={showOnlyActive ? "default" : "outline"}
              size="default"
              onClick={() => setShowOnlyActive(!showOnlyActive)}
            >
              <TrendingUp className="h-4 w-4 mr-2" />
              {showOnlyActive ? "Nur aktive Konten" : "Alle Konten"}
            </Button>

            {/* View Mode Toggle */}
            <Button
              variant={viewMode === "percentage" ? "default" : "outline"}
              size="default"
              onClick={() =>
                setViewMode(viewMode === "absolute" ? "percentage" : "absolute")
              }
            >
              <Percent className="h-4 w-4 mr-2" />
              {viewMode === "percentage" ? "Prozentual" : "Absolut"}
            </Button>
          </div>

          {error && (
            <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 shadow-sm">
              <p className="text-sm text-destructive font-medium">
                ⚠️ Fehler beim Laden der Konten. Bitte versuchen Sie es erneut.
              </p>
            </div>
          )}

          {loading && (
            <div className="space-y-4">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          )}

          {/* Accounts Period Grid */}
          {!loading && !error && filteredAccounts.length > 0 && (
            <div className="bg-card rounded-lg border shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                {/* Header Row */}
                <div className="flex bg-muted border-b sticky top-0 z-20">
                  {/* Sticky Columns */}
                  <div className="sticky left-0 z-30 flex bg-muted border-r-2">
                    <div className="w-[120px] min-w-[120px] max-w-[120px] px-4 py-3 font-semibold text-sm">
                      Konto
                    </div>
                    <div className="w-[280px] min-w-[280px] max-w-[280px] px-4 py-3 font-semibold text-sm border-l">
                      Kontoname
                    </div>
                  </div>

                  {/* Period Columns */}
                  <div className="flex bg-muted">
                    {filteredAccounts[0]?.periodBalances.map(
                      (period, index, array) => (
                        <div
                          key={period.periodKey}
                          className={`w-[140px] min-w-[140px] max-w-[140px] px-4 py-3 font-semibold text-sm text-right border-l ${
                            index === array.length - 1 ? "border-r" : ""
                          }`}
                        >
                          {formatPeriodLabel(period.periodLabel)}
                        </div>
                      ),
                    )}
                  </div>
                </div>

                {/* Data Rows */}
                <div>
                  {filteredAccounts.map((account) => (
                    <div key={account.journalAccountId} className="flex">
                      {/* Sticky Columns */}
                      <div className="sticky left-0 z-10 flex bg-card border-r-2 border-b">
                        <div className="w-[120px] min-w-[120px] max-w-[120px] px-4 py-3 font-mono text-sm font-medium flex items-center">
                          {account.accountNumber || "—"}
                        </div>
                        <div className="w-[280px] min-w-[280px] max-w-[280px] px-4 py-3 text-sm border-l flex items-center">
                          <span
                            className="truncate flex-1"
                            title={account.accountName}
                          >
                            {account.accountName}
                          </span>
                        </div>
                      </div>

                      {/* Period Columns */}
                      <div className="flex">
                        {account.periodBalances.map((period, index, array) => {
                          const isValidActivity = Number.isFinite(
                            period.balance,
                          );

                          // Calculate percentage change for percentage view
                          let percentageChange: number | null = null;
                          let previousActivity = 0;

                          if (viewMode === "percentage" && isValidActivity) {
                            // Find the corresponding account in the full data (with previous period)
                            const fullAccount = accountPeriodBalances.find(
                              (a) =>
                                a.journalAccountId === account.journalAccountId,
                            );

                            if (fullAccount) {
                              // Find the index of the current period in the full data
                              const fullPeriodIndex =
                                fullAccount.periodBalances.findIndex(
                                  (p) => p.periodKey === period.periodKey,
                                );

                              // Get the previous period from full data (which includes the extra period)
                              if (fullPeriodIndex > 0) {
                                const prevPeriodBalance =
                                  fullAccount.periodBalances[
                                    fullPeriodIndex - 1
                                  ]?.balance;
                                // Convert to number and ensure it's valid
                                previousActivity = Number.isFinite(
                                  prevPeriodBalance,
                                )
                                  ? prevPeriodBalance
                                  : 0;
                                percentageChange = calculatePercentageChange(
                                  period.balance,
                                  previousActivity,
                                );
                              }
                            }
                          }

                          // Parse entryCount early
                          const entryCount =
                            typeof period.entryCount === "string"
                              ? Number.parseInt(period.entryCount, 10)
                              : period.entryCount;

                          // Determine display value and color
                          const displayValue =
                            viewMode === "absolute"
                              ? !isValidActivity || period.balance === 0
                                ? "—"
                                : formatAmount(period.balance, "EUR")
                              : percentageChange === null
                                ? "—" // No previous period to compare or invalid data
                                : entryCount === 0
                                  ? "—" // No data in this period
                                  : formatPercentage(percentageChange);

                          // Color coding based on view mode
                          let colorClass = "";
                          if (viewMode === "absolute") {
                            colorClass =
                              period.balance === 0
                                ? "text-muted-foreground"
                                : period.balance < 0
                                  ? "text-red-600 font-medium"
                                  : "";
                          } else if (
                            viewMode === "percentage" &&
                            percentageChange !== null &&
                            entryCount > 0
                          ) {
                            const absChange = Math.abs(percentageChange);
                            if (percentageChange === 0) {
                              colorClass = "text-muted-foreground";
                            } else if (absChange >= 50) {
                              colorClass =
                                percentageChange > 0
                                  ? "text-green-700 font-bold bg-green-50"
                                  : "text-red-700 font-bold bg-red-50";
                            } else if (absChange >= 25) {
                              colorClass =
                                percentageChange > 0
                                  ? "text-green-600 font-semibold"
                                  : "text-red-600 font-semibold";
                            } else if (absChange >= 10) {
                              colorClass =
                                percentageChange > 0
                                  ? "text-green-600 font-medium"
                                  : "text-red-600 font-medium";
                            } else {
                              colorClass =
                                percentageChange > 0
                                  ? "text-green-600"
                                  : "text-red-600";
                            }
                          }

                          const tooltip =
                            entryCount > 0
                              ? `${formatPeriodLabel(period.periodLabel)}\nSoll: ${formatAmount(period.debitTotal, "EUR")}\nHaben: ${formatAmount(period.creditTotal, "EUR")}\nAktivität: ${formatAmount(period.balance, "EUR")}\nBuchungen: ${entryCount}${
                                  viewMode === "percentage" &&
                                  percentageChange !== null
                                    ? `\n\nÄnderung: ${formatPercentage(percentageChange)}\nVorperiode: ${formatAmount(previousActivity, "EUR")}`
                                    : ""
                                }\n\nKlicken für Details`
                              : `${formatPeriodLabel(period.periodLabel)}: Keine Buchungen`;

                          return (
                            <div
                              key={period.periodKey}
                              className={`w-[140px] min-w-[140px] max-w-[140px] px-4 py-3 text-sm text-right font-mono border-l border-b flex items-center justify-end transition-all ${
                                index === array.length - 1 ? "border-r" : ""
                              } ${
                                entryCount > 0
                                  ? "hover:bg-muted/60 hover:ring-1 hover:ring-primary/20 cursor-pointer"
                                  : ""
                              } ${colorClass}`}
                              title={tooltip}
                              onClick={
                                entryCount > 0
                                  ? (e) =>
                                      handlePeriodClick(
                                        e,
                                        account.journalAccountId,
                                        account.accountNumber,
                                        account.accountName,
                                        period.startDate,
                                        period.endDate,
                                        formatPeriodLabel(period.periodLabel),
                                      )
                                  : undefined
                              }
                              onKeyDown={
                                entryCount > 0
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        handlePeriodClick(
                                          e as unknown as React.MouseEvent,
                                          account.journalAccountId,
                                          account.accountNumber,
                                          account.accountName,
                                          period.startDate,
                                          period.endDate,
                                          formatPeriodLabel(period.periodLabel),
                                        );
                                      }
                                    }
                                  : undefined
                              }
                              role={entryCount > 0 ? "button" : undefined}
                              tabIndex={entryCount > 0 ? 0 : undefined}
                            >
                              <span className="truncate">{displayValue}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Empty State - No Accounts */}
          {!loading && !error && displayedPeriodBalances?.length === 0 && (
            <div className="flex flex-col items-center justify-center h-[400px] gap-4 text-muted-foreground bg-card rounded-lg border shadow-sm">
              <div className="text-6xl">💼</div>
              <div className="text-center">
                <h3 className="text-xl font-semibold mb-2 text-foreground">
                  Keine aktiven Konten
                </h3>
                <p className="text-sm max-w-md">
                  {showOnlyActive
                    ? "Im gewählten Zeitraum gibt es keine Konten mit Buchungen. Versuchen Sie einen anderen Zeitraum oder zeigen Sie alle Konten an."
                    : "Es wurden noch keine Konten angelegt. Bitte fügen Sie Konten in den Einstellungen hinzu."}
                </p>
              </div>
            </div>
          )}

          {/* Empty State - No Search Results */}
          {!loading &&
            !error &&
            displayedPeriodBalances &&
            displayedPeriodBalances.length > 0 &&
            filteredAccounts.length === 0 && (
              <div className="flex flex-col items-center justify-center h-[400px] gap-4 text-muted-foreground bg-card rounded-lg border shadow-sm">
                <div className="text-6xl">🔍</div>
                <div className="text-center">
                  <h3 className="text-xl font-semibold mb-2 text-foreground">
                    Keine Ergebnisse
                  </h3>
                  <p className="text-sm max-w-md">
                    Keine Konten entsprechen Ihrer Suche "{searchQuery}".
                    Versuchen Sie einen anderen Suchbegriff.
                  </p>
                </div>
              </div>
            )}

          {/* Stats */}
          {!loading && !error && displayedPeriodBalances && (
            <div className="text-sm text-muted-foreground text-center">
              {filteredAccounts.length} von {displayedPeriodBalances.length}{" "}
              Kont
              {displayedPeriodBalances.length !== 1 ? "en" : "o"} angezeigt
              {showOnlyActive && " (nur aktive)"}
            </div>
          )}
        </div>

        {/* Account Period Detail Sheet */}
        <AccountPeriodSheet
          open={sheetState.open}
          onOpenChange={(open) => setSheetState((prev) => ({ ...prev, open }))}
          accountId={sheetState.accountId}
          accountNumber={sheetState.accountNumber}
          accountName={sheetState.accountName}
          periodStartDate={sheetState.periodStartDate}
          periodEndDate={sheetState.periodEndDate}
          periodLabel={sheetState.periodLabel}
        />
      </div>
    </MainLayout>
  );
}

AccountsListPage.displayName = "AccountsListPage";
